import { useTranslation } from '../i18n';
import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import worldJson from './world.json';
import { normalizeCountryCode } from './CountryFlag';

// <img> flag for the HTML tooltip ('' when the code has no flag)
const flagHtml = (code) => {
  const c = normalizeCountryCode(code);
  return c ? `<img src="${process.env.PUBLIC_URL || ''}/flags/${c}.svg" width="18" height="12" alt="" aria-hidden="true" style="vertical-align:-1px;margin-right:6px;border-radius:2px;object-fit:cover;box-shadow:0 0 0 1px rgba(128,128,128,.25)">` : '';
};

// 注册世界地图
echarts.registerMap('world', worldJson);

// 国家代码到名称的映射（常用国家）
const countryCodeToName = {
  'CN': 'China',
  'US': 'United States',
  'JP': 'Japan',
  'KR': 'Korea',
  'DE': 'Germany',
  'FR': 'France',
  'GB': 'United Kingdom',
  'RU': 'Russia',
  'BR': 'Brazil',
  'IN': 'India',
  'AU': 'Australia',
  'CA': 'Canada',
  'IT': 'Italy',
  'ES': 'Spain',
  'NL': 'Netherlands',
  'SG': 'Singapore',
  'HK': 'Hong Kong',
  'TW': 'Taiwan',
  'TH': 'Thailand',
  'VN': 'Vietnam',
  'MY': 'Malaysia',
  'ID': 'Indonesia',
  'PH': 'Philippines',
  'MX': 'Mexico',
  'AR': 'Argentina',
  'ZA': 'South Africa',
  'AE': 'United Arab Emirates',
  'SA': 'Saudi Arabia',
  'TR': 'Turkey',
  'PL': 'Poland',
  'SE': 'Sweden',
  'NO': 'Norway',
  'FI': 'Finland',
  'DK': 'Denmark',
  'CH': 'Switzerland',
  'AT': 'Austria',
  'BE': 'Belgium',
  'IE': 'Ireland',
  'PT': 'Portugal',
  'CZ': 'Czech Republic',
  'NZ': 'New Zealand',
  'IL': 'Israel',
  'UA': 'Ukraine',
  'EG': 'Egypt',
  'NG': 'Nigeria',
  'KE': 'Kenya',
  'CL': 'Chile',
  'CO': 'Colombia',
  'PE': 'Peru',
  'PK': 'Pakistan',
  'BD': 'Bangladesh'
};

const WorldMap = ({
  data = [],
  title = '全球流量分布',
  valueField = 'requests',
  valueLabel = '请求数',
  colorRange = ['#e0f3f8', '#abd9e9', '#74add1', '#4575b4', '#313695'],
  height = 500,
  showScatter = true
}) => {
  const { t } = useTranslation();
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!chartRef.current) return;

    // 初始化或获取实例
    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    const chart = chartInstance.current;

    // 处理数据 - 转换为 ECharts 格式
    const mapData = data.map(item => {
      const countryName = countryCodeToName[item.country_code] || item.country_name || item.country_code;
      return {
        name: countryName,
        value: item[valueField] || 0,
        ...item
      };
    });

    // 散点数据（用于显示具体位置）
    const scatterData = data
      .filter(item => item.latitude && item.longitude)
      .map(item => ({
        name: item.country_name || item.country_code,
        value: [item.longitude, item.latitude, item[valueField] || 0],
        ...item
      }));

    // 计算最大值用于颜色映射
    const maxValue = Math.max(...mapData.map(d => d.value), 1);

    const option = {
      title: {
        text: t(title),
        left: 'center',
        textStyle: {
          fontSize: 16,
          fontWeight: 'normal'
        }
      },
      tooltip: {
        trigger: 'item',
        formatter: (params) => {
          if (params.seriesType === 'scatter') {
            const d = params.data;
            return `
              ${flagHtml(d.country_code)}<strong>${d.name}</strong><br/>
              ${t(valueLabel)}: ${(d.value[2] || 0).toLocaleString()}<br/>
              ${d.traffic ? t('Traffic: {{value}}', { value: formatBytes(d.traffic) }) : ''}
            `;
          }
          if (params.value) {
            return `
              ${flagHtml(params.data && params.data.country_code)}<strong>${params.name}</strong><br/>
              ${t(valueLabel)}: ${(params.value || 0).toLocaleString()}
            `;
          }
          return t('<strong>{{name}}</strong><br/>No data', { name: params.name });
        }
      },
      visualMap: {
        min: 0,
        itemWidth: 10,
        itemHeight: 80,
        max: maxValue,
        text: [t('High'), t('Low')],
        realtime: false,
        calculable: false,
        inRange: {
          color: colorRange
        },
        left: 'left',
        top: 'bottom'
      },
      geo: {
        map: 'world',
        roam: true,
        zoom: 1.2,
        scaleLimit: {
          min: 1,
          max: 10
        },
        itemStyle: {
          areaColor: '#f5f5f5',
          borderColor: '#d9d9d9',
          borderWidth: 0.5
        },
        emphasis: {
          itemStyle: {
            areaColor: '#ffd666'
          },
          label: {
            show: true
          }
        }
      },
      series: [
        {
          name: t(valueLabel),
          type: 'map',
          map: 'world',
          geoIndex: 0,
          data: mapData
        },
        ...(showScatter && scatterData.length > 0 ? [{
          name: t('Location'),
          type: 'scatter',
          coordinateSystem: 'geo',
          data: scatterData,
          symbolSize: (val) => 4 + 12 * Math.sqrt((val[2] || 0) / maxValue), // 按最大值归一,直径 4~16px
          itemStyle: {
            color: 'rgba(22, 119, 255, 0.75)',
            borderColor: '#fff',
            borderWidth: 1
          },
          encode: {
            value: 2
          }
        }] : [])
      ]
    };

    chart.setOption(option);

    // 响应式
    const handleResize = () => {
      chart.resize();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [data, title, valueField, valueLabel, colorRange, showScatter, t]);

  // 清理
  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, []);

  return (
    <div
      ref={chartRef}
      style={{ width: '100%', height: height }}
    />
  );
};

// 格式化字节
function formatBytes(bytes) {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export default WorldMap;
